import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, FileBarChart, TrendingUp, TrendingDown, PiggyBank, Loader2, ChevronDown, Check, LineChart as LineIcon, Download, FileSpreadsheet, Sparkles, Plus, Save, Trash2, GitCompare, X, Home, MapPin, RefreshCw, Landmark, Info } from "lucide-react";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { Tooltip as UITooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../../components/ui/tooltip";
import api from "../../lib/api";
import { ForecastGuide } from "../../components/guides/ForecastGuide";
import { ValuationGuide } from "../../components/guides/ValuationGuide";
import { AnnualReportButton } from "../../components/brand/AnnualReportButton";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const SRC_LABEL = { cerema: "Cerema DVF+ (API Données Foncières)", geodvf: "Etalab geo-dvf (open-data)" };

// Info-bulle de calcul : affiche un titre, une liste label→valeur, un total en gras et une note.
function CalcTip({ title, rows = [], total, note, children }) {
  return (
    <UITooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex items-center gap-1 cursor-help">{children}<Info className="w-3 h-3 text-brand-green/35 flex-shrink-0" /></span>
      </TooltipTrigger>
      <TooltipContent side="left" className="max-w-[300px] bg-brand-greenDeep text-white border border-brand-lime/30 shadow-xl px-3.5 py-3 text-left">
        <p className="font-display font-semibold text-sm text-brand-lime mb-2">{title}</p>
        <div className="space-y-1">
          {rows.map((r, i) => (
            <div key={i} className={`flex items-baseline justify-between gap-4 text-[11px] ${r.strong ? "font-semibold text-white" : "text-white/75"}`}>
              <span>{r.label}</span>
              <span className="whitespace-nowrap tabular-nums">{r.value}</span>
            </div>
          ))}
        </div>
        {total && (
          <div className="flex items-baseline justify-between gap-4 text-xs font-bold text-brand-lime mt-2 pt-2 border-t border-white/15">
            <span>{total.label}</span><span className="whitespace-nowrap tabular-nums">{total.value}</span>
          </div>
        )}
        {note && <p className="text-[10px] text-white/45 mt-2 leading-snug">{note}</p>}
      </TooltipContent>
    </UITooltip>
  );
}
const fmt2 = (n) => (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const YEARS = [2026, 2025, 2024, 2023, 2022, 2021, 2020, 2019, 2018, 2017];
const PERIODS = [
  { code: "current_year", label: "Année en cours" },
  { code: "6", label: "6 mois" },
  { code: "12", label: "12 mois" },
  { code: "24", label: "24 mois" },
  { code: "36", label: "36 mois" },
  { code: "48", label: "48 mois" },
  { code: "60", label: "60 mois" },
];

function PropertyMultiSelect({ properties, selected, setSelected }) {
  const [open, setOpen] = useState(false);
  const allSelected = selected.length === 0;
  const toggle = (name) => {
    setSelected((prev) => (prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name]));
  };
  const label = allSelected ? "Tous les biens" : `${selected.length} bien(s)`;
  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)} className="inp flex items-center justify-between gap-2 text-left" data-testid="filter-properties">
        <span className="truncate">{label}</span>
        <ChevronDown className="w-4 h-4 shrink-0 text-brand-green/40" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto bg-white rounded-xl border border-brand-soft shadow-lg py-1" data-testid="properties-panel">
            <button onClick={() => setSelected([])} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-brand-softer text-brand-greenDeep" data-testid="prop-opt-all">
              <span className={`w-4 h-4 rounded border flex items-center justify-center ${allSelected ? "bg-brand-lime border-brand-limeDark" : "border-brand-soft"}`}>{allSelected && <Check className="w-3 h-3 text-brand-greenDeep" />}</span>
              Tous les biens
            </button>
            {properties.map((p) => {
              const on = selected.includes(p.name);
              return (
                <button key={p.id} onClick={() => toggle(p.name)} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-brand-softer text-brand-greenDeep" data-testid={`prop-opt-${p.id}`}>
                  <span className={`w-4 h-4 rounded border flex items-center justify-center ${on ? "bg-brand-lime border-brand-limeDark" : "border-brand-soft"}`}>{on && <Check className="w-3 h-3 text-brand-greenDeep" />}</span>
                  <span className="truncate">{p.name}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function TotalCard({ icon: Icon, label, value, tone, prev, prevLabel, delta }) {
  const tones = {
    green: "bg-brand-emerald/10 text-brand-emerald",
    orange: "bg-orange-100 text-orange-600",
    gold: "bg-brand-lime/30 text-brand-greenDeep",
  };
  const pct = prev !== undefined && prev !== 0 ? Math.round((delta / Math.abs(prev)) * 100) : null;
  const up = (delta || 0) >= 0;
  return (
    <div className="bg-white rounded-2xl border border-brand-soft p-5" data-testid={`total-${label}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className={`w-9 h-9 rounded-xl flex items-center justify-center ${tones[tone]}`}><Icon className="w-4 h-4" /></span>
        <p className="text-sm text-brand-green/60 font-medium">{label}</p>
      </div>
      <p className="font-display text-2xl font-semibold text-brand-greenDeep">{fmt2(value)} €</p>
      {prev !== undefined && (
        <div className="mt-2 flex items-center gap-2 text-xs" data-testid={`total-prev-${label}`}>
          <span className="text-brand-green/45">{prevLabel} : {fmt2(prev)} €</span>
          <span className={`inline-flex items-center gap-0.5 font-semibold ${up ? "text-brand-emerald" : "text-red-600"}`}>
            {up ? "▲" : "▼"} {fmt2(Math.abs(delta))} €{pct !== null ? ` (${up ? "+" : "-"}${Math.abs(pct)}%)` : ""}
          </span>
        </div>
      )}
    </div>
  );
}

function CategoryList({ title, rows, tone, prevYear }) {
  const total = rows.reduce((s, r) => s + r.amount, 0) || 1;
  return (
    <div className="bg-white rounded-2xl border border-brand-soft p-5">
      <h4 className="font-semibold text-brand-greenDeep mb-3">{title}</h4>
      {rows.length === 0 ? (
        <p className="text-sm text-brand-green/45">Aucune donnée sur la période.</p>
      ) : (
        <div className="space-y-2.5">
          {rows.map((r) => (
            <div key={r.category} data-testid={`cat-${r.category}`}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-brand-green/75 truncate pr-2">{r.category}</span>
                <span className="font-semibold text-brand-greenDeep whitespace-nowrap">{fmt2(r.amount)} €</span>
              </div>
              <div className="h-1.5 rounded-full bg-brand-softer overflow-hidden">
                <div className={`h-full ${tone === "green" ? "bg-brand-emerald" : "bg-orange-400"}`} style={{ width: `${Math.max(3, (r.amount / total) * 100)}%` }} />
              </div>
              {r.amount_prev !== undefined && (
                <p className="text-[11px] text-brand-green/40 mt-0.5">{prevYear} : {fmt2(r.amount_prev)} €</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BilanPrevisionnel() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("bilan");
  const [owners, setOwners] = useState([]);
  const [properties, setProperties] = useState([]);
  // shared filters
  const [owner, setOwner] = useState("");
  const [selProps, setSelProps] = useState([]);
  // bilan
  const [year, setYear] = useState(2026);
  const [bilan, setBilan] = useState(null);
  // previsionnel
  const [period, setPeriod] = useState("current_year");
  const [prev, setPrev] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState("");
  // scenario
  const [scForm, setScForm] = useState({ rent_amount: "", rent_start: "", loan_amount: "", loan_months: "", loan_rate: "", loan_ins: "", loan_start: "" });
  const [scenario, setScenario] = useState(null);
  const [scBusy, setScBusy] = useState(false);
  // saved scenarios
  const [saved, setSaved] = useState([]);
  const [selSaved, setSelSaved] = useState([]);
  const [compareData, setCompareData] = useState(null);
  const [compareBusy, setCompareBusy] = useState(false);
  const [includeBase, setIncludeBase] = useState(true);
  const [nameModal, setNameModal] = useState(false);
  const [scenarioName, setScenarioName] = useState("");
  const [savingName, setSavingName] = useState(false);
  // rapport financier IA (Bilan)
  const [aiReport, setAiReport] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  // rapport prévisionnel IA
  const [pvReport, setPvReport] = useState(null);
  const [pvBusy, setPvBusy] = useState(false);

  const [patrimoine, setPatrimoine] = useState(null);
  const [patriBusy, setPatriBusy] = useState(false);
  const [editProp, setEditProp] = useState(null);

  useEffect(() => {
    api.get("/owners").then((r) => setOwners(r.data || [])).catch(() => {});
    api.get("/properties").then((r) => setProperties(r.data || [])).catch(() => {});
  }, []);

  const propsParam = useMemo(() => (selProps.length ? selProps.join("|") : ""), [selProps]);

  useEffect(() => {
    if (tab !== "bilan") return;
    setLoading(true);
    setAiReport(null);
    api.get("/finances/bilan", { params: { year, owner, properties: propsParam } })
      .then((r) => setBilan(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [tab, year, owner, propsParam]);

  useEffect(() => {
    if (tab !== "previsionnel") return;
    setLoading(true);
    setScenario(null);
    setPvReport(null);
    api.get("/finances/previsionnel", { params: { period, owner, properties: propsParam } })
      .then((r) => setPrev(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [tab, period, owner, propsParam]);

  const loadPatrimoine = () => {
    setPatriBusy(true);
    api.get("/finances/patrimoine", { params: { owner, properties: propsParam } })
      .then((r) => setPatrimoine(r.data)).catch(() => toast.error("Estimation indisponible")).finally(() => setPatriBusy(false));
  };
  const refreshPatrimoine = () => {
    setPatriBusy(true);
    api.post("/finances/patrimoine/refresh", null, { params: { owner, properties: propsParam } })
      .then((r) => { setPatrimoine(r.data); toast.success("Estimations actualisées depuis les données DVF"); })
      .catch(() => toast.error("Actualisation impossible (service DVF)")).finally(() => setPatriBusy(false));
  };
  useEffect(() => {
    if (tab !== "patrimoine") return;
    loadPatrimoine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, owner, propsParam]);

  const exportBilan = async (format) => {
    setExporting(format);
    try {
      const res = await api.get("/finances/bilan-export", { params: { year, owner, properties: propsParam, format }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `bilan-${year}.${format === "xlsx" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Export impossible"); }
    finally { setExporting(""); }
  };

  const buildAiScope = () => {
    const ownerLabel = owner || "Tous les propriétaires";
    const propLabel = selProps.length ? `${selProps.length} bien(s)` : "Tous les biens";
    return `Année ${year} · ${ownerLabel} · ${propLabel}`;
  };

  const generateAiReport = async () => {
    if (!bilan) return;
    setAiBusy(true);
    try {
      const by_category = {};
      (bilan.expense_by_category || []).forEach((r) => { by_category[r.category] = r.amount; });
      const income_by_category = {};
      (bilan.revenue_by_category || []).forEach((r) => { income_by_category[r.category] = r.amount; });
      const { data } = await api.post("/ai/financial-report", {
        scope: buildAiScope(), year,
        revenus: bilan.revenue_total, charges: bilan.expense_total,
        by_category, income_by_category,
      });
      setAiReport(data);
      toast.success("Rapport IA généré");
    } catch { toast.error("Génération impossible"); }
    finally { setAiBusy(false); }
  };

  const exportAiReportPdf = async () => {
    if (!aiReport) return;
    setAiBusy(true);
    try {
      const res = await api.post("/ai/financial-report/pdf", {
        kpis: aiReport.kpis, summary: aiReport.summary, highlights: aiReport.highlights,
        market_comparison: aiReport.market_comparison, recommendations: aiReport.recommendations,
        scope: aiReport.scope || buildAiScope(),
      }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `rapport-financier-ia-${year}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF téléchargé");
    } catch { toast.error("Export impossible"); }
    finally { setAiBusy(false); }
  };

  const generatePvReport = async () => {
    if (!prev) return;
    setPvBusy(true);
    try {
      const ownerLabel = owner || "Tous les propriétaires";
      const propLabel = selProps.length ? `${selProps.length} bien(s)` : "Tous les biens";
      const payload = {
        scope: `Projection ${prev.months} mois · ${ownerLabel} · ${propLabel}`,
        months: prev.months,
        revenue_total: prev.revenue_total,
        expense_total: prev.expense_total,
        net: prev.net,
        monthly: (prev.monthly || []).map((m) => ({ label: m.label, cumulative_net: m.cumulative_net })),
        has_scenario: false,
      };
      if (scenario) {
        payload.has_scenario = true;
        payload.revenue_total = scenario.scenario.revenue_total;
        payload.expense_total = scenario.scenario.expense_total;
        payload.net = scenario.scenario.net;
        payload.scenario_net = scenario.scenario.net;
        payload.delta_net = scenario.delta.net;
        payload.delta_revenue = scenario.delta.revenue;
        payload.delta_expense = scenario.delta.expense;
        payload.monthly = (scenario.scenario.monthly || []).map((m) => ({ label: m.label, cumulative_net: m.cumulative_net }));
        if (scForm.rent_amount) payload.extra_rent = Number(scForm.rent_amount) || 0;
        if (scForm.loan_amount) payload.extra_loan_amount = Number(scForm.loan_amount) || 0;
      }
      const { data } = await api.post("/ai/previsionnel-report", payload);
      setPvReport(data);
      toast.success("Rapport prévisionnel IA généré");
    } catch { toast.error("Génération impossible"); }
    finally { setPvBusy(false); }
  };

  const runScenario = async () => {
    const body = { period, owner, properties: propsParam };
    if (scForm.rent_amount) body.extra_rent = { amount: Number(scForm.rent_amount) || 0, start_date: scForm.rent_start || "" };
    if (scForm.loan_amount && scForm.loan_months) body.extra_loan = { amount: Number(scForm.loan_amount) || 0, months: Number(scForm.loan_months) || 0, rate: Number(scForm.loan_rate) || 0, insurance_rate: Number(scForm.loan_ins) || 0, start_date: scForm.loan_start || "" };
    if (!body.extra_rent && !body.extra_loan) return toast.error("Renseignez un nouveau loyer ou un nouveau prêt");
    setScBusy(true);
    try {
      const res = await api.post("/finances/previsionnel-scenario", body);
      setScenario(res.data);
      toast.success("Scénario calculé");
    } catch { toast.error("Simulation impossible"); }
    finally { setScBusy(false); }
  };
  const scSet = (k, v) => setScForm((s) => ({ ...s, [k]: v }));

  const loadSaved = () => api.get("/finances/scenarios").then((r) => setSaved(r.data || [])).catch(() => {});
  useEffect(() => { loadSaved(); }, []);

  const exportPrevisionnel = async (format) => {
    setExporting(`pv-${format}`);
    try {
      const res = await api.get("/finances/previsionnel-export", { params: { period, owner, properties: propsParam, format }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `previsionnel.${format === "xlsx" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Export impossible"); }
    finally { setExporting(""); }
  };

  const saveScenario = async () => {
    if (!scenarioName.trim()) return toast.error("Nom du scénario requis");
    const body = { name: scenarioName.trim(), period, owner, properties: propsParam };
    if (scForm.rent_amount) body.extra_rent = { amount: Number(scForm.rent_amount) || 0, start_date: scForm.rent_start || "" };
    if (scForm.loan_amount && scForm.loan_months) body.extra_loan = { amount: Number(scForm.loan_amount) || 0, months: Number(scForm.loan_months) || 0, rate: Number(scForm.loan_rate) || 0, insurance_rate: Number(scForm.loan_ins) || 0, start_date: scForm.loan_start || "" };
    setSavingName(true);
    try {
      await api.post("/finances/scenarios", body);
      toast.success("Scénario enregistré");
      setNameModal(false); setScenarioName("");
      loadSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible"); }
    finally { setSavingName(false); }
  };

  const toggleSaved = (id) => setSelSaved((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const runCompare = async () => {
    if (selSaved.length < 1 && !includeBase) return toast.error("Sélectionnez au moins un scénario ou incluez la projection de base");
    setCompareBusy(true);
    try {
      const res = await api.post("/finances/scenarios/compare", { ids: selSaved, include_base: includeBase, period, owner, properties: propsParam });
      setCompareData(res.data.scenarios || []);
    } catch { toast.error("Comparaison impossible"); }
    finally { setCompareBusy(false); }
  };

  const deleteSaved = async (id) => {
    if (!window.confirm("Supprimer ce scénario ?")) return;
    try {
      await api.delete(`/finances/scenarios/${id}`);
      setSaved((p) => p.filter((x) => x.id !== id));
      setSelSaved((p) => p.filter((x) => x !== id));
      setCompareData(null);
      toast.success("Scénario supprimé");
    } catch { toast.error("Erreur"); }
  };

  const COMPARE_COLORS = ["#c9a24b", "#2f8f6b", "#e08a4a", "#6b7cff", "#c94b8b", "#4bb8c9"];
  const compareChart = useMemo(() => {
    if (!compareData || !compareData.length) return [];
    const labels = compareData.reduce((acc, s) => (s.monthly.length > acc.length ? s.monthly.map((m) => m.label) : acc), []);
    return labels.map((label, i) => {
      const row = { label };
      compareData.forEach((s) => { row[s.id] = s.monthly[i]?.cumulative_net ?? null; });
      return row;
    });
  }, [compareData]);

  return (
    <div className="space-y-6" data-testid="bilan-page">
      <div className="flex flex-wrap justify-end gap-2 -mb-3"><AnnualReportButton /><ForecastGuide /><ValuationGuide /></div>
      <div className="flex items-center justify-between">
        <button onClick={() => navigate("/app/finances")} className="inline-flex items-center gap-2 text-sm text-brand-green/60 hover:text-brand-greenDeep font-medium" data-testid="bilan-back">
          <ArrowLeft className="w-4 h-4" /> Retour aux finances
        </button>
      </div>

      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-brand-lime/30 text-brand-greenDeep flex items-center justify-center"><FileBarChart className="w-5 h-5" /></div>
        <div>
          <h2 className="font-display text-2xl font-semibold text-brand-greenDeep">Bilan, Prévisionnel &amp; Estimation Patrimoniale</h2>
          <p className="text-sm text-brand-green/55">Analysez vos résultats, projetez vos flux futurs et estimez la valeur de votre patrimoine.</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 p-1 bg-brand-softer rounded-full w-fit">
        <button onClick={() => setTab("bilan")} className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${tab === "bilan" ? "bg-white shadow text-brand-greenDeep" : "text-brand-green/55"}`} data-testid="tab-bilan">Vue Bilan</button>
        <button onClick={() => setTab("previsionnel")} className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${tab === "previsionnel" ? "bg-white shadow text-brand-greenDeep" : "text-brand-green/55"}`} data-testid="tab-previsionnel">Vue Prévisionnel</button>
        <button onClick={() => setTab("patrimoine")} className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${tab === "patrimoine" ? "bg-white shadow text-brand-greenDeep" : "text-brand-green/55"}`} data-testid="tab-patrimoine">Vue Patrimoniale</button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-brand-soft p-5">
        <div className="grid sm:grid-cols-3 gap-3">
          {tab === "bilan" ? (
            <label className="block">
              <span className="block text-xs font-medium text-brand-green/70 mb-1.5">Année fiscale</span>
              <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="inp" data-testid="filter-year">
                {YEARS.map((y) => <option key={y} value={y}>{`Année ${y}`}</option>)}
              </select>
            </label>
          ) : tab === "previsionnel" ? (
            <label className="block">
              <span className="block text-xs font-medium text-brand-green/70 mb-1.5">Période de prévisionnel</span>
              <select value={period} onChange={(e) => setPeriod(e.target.value)} className="inp" data-testid="filter-period">
                {PERIODS.map((p) => <option key={p.code} value={p.code}>{p.label}</option>)}
              </select>
            </label>
          ) : (
            <div className="hidden sm:block" />
          )}
          <label className="block">
            <span className="block text-xs font-medium text-brand-green/70 mb-1.5">Propriétaire</span>
            <select value={owner} onChange={(e) => setOwner(e.target.value)} className="inp" data-testid="filter-owner">
              <option value="">Tous les propriétaires</option>
              {owners.map((o) => <option key={o.id} value={o.name}>{o.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-brand-green/70 mb-1.5">Biens</span>
            <PropertyMultiSelect properties={properties} selected={selProps} setSelected={setSelProps} />
          </label>
        </div>
      </div>

      {loading && <div className="py-12 text-center text-brand-green/50"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}

      {/* PATRIMOINE */}
      {tab === "patrimoine" && (
        <TooltipProvider delayDuration={100}>
        <div className="space-y-5" data-testid="patrimoine-content">
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="bg-brand-greenDeep text-white rounded-2xl p-5" data-testid="patri-total">
              <div className="flex items-center gap-2 text-white/70 text-xs font-medium"><Home className="w-4 h-4" /> Estimation patrimoniale totale</div>
              <p className="mt-2 font-display text-2xl font-semibold">{fmt(patrimoine?.total_estimated)} €</p>
              <p className="text-white/50 text-xs mt-1">{patrimoine?.total_low ? `Fourchette : ${fmt(patrimoine.total_low)} € – ${fmt(patrimoine.total_high)} €` : "Basé sur les ventes récentes DVF"}</p>
            </div>
            <div className="bg-white border border-brand-soft rounded-2xl p-5">
              <div className="flex items-center gap-2 text-brand-green/60 text-xs font-medium"><Landmark className="w-4 h-4" /> Biens évalués</div>
              <p className="mt-2 font-display text-2xl font-semibold text-brand-greenDeep">{(patrimoine?.items || []).filter((i) => i.estimated_value).length}/{(patrimoine?.items || []).length}</p>
              <p className="text-brand-green/45 text-xs mt-1">Estimations disponibles</p>
            </div>
            <div className="bg-white border border-brand-soft rounded-2xl p-5 flex flex-col justify-between">
              <div className="flex items-center gap-2 text-brand-green/60 text-xs font-medium"><MapPin className="w-4 h-4" /> Source des données</div>
              <p className="mt-2 text-sm font-medium text-brand-greenDeep leading-snug">DVF+ open-data — Cerema / Etalab<br /><span className="text-brand-green/45 text-xs">{patrimoine?.last_updated ? `Actualisé le ${new Date(patrimoine.last_updated).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}` : "Données stockées — cliquez sur Actualiser"}</span></p>
              <button onClick={refreshPatrimoine} disabled={patriBusy} className="mt-2 self-start inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-soft hover:bg-brand-mint text-brand-greenDeep font-semibold text-xs disabled:opacity-60" data-testid="patri-refresh">
                {patriBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Actualiser
              </button>
            </div>
          </div>

          {patriBusy && !patrimoine && <div className="py-12 text-center text-brand-green/50"><Loader2 className="w-6 h-6 animate-spin mx-auto" /><p className="mt-2 text-sm">Interrogation des données foncières (DVF)…</p></div>}

          {patrimoine && (
            <div className="bg-white rounded-2xl border border-brand-soft overflow-hidden">
              <div className="px-5 py-4 border-b border-brand-soft flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <Home className="w-4 h-4 text-brand-greenDeep" />
                  <h3 className="font-semibold text-brand-greenDeep text-sm">Estimation des biens immobiliers</h3>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => window.open(`${BACKEND_URL}/api/finances/patrimoine-export?format=xlsx&owner=${encodeURIComponent(owner)}&properties=${encodeURIComponent(propsParam)}`, "_blank")} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-soft hover:bg-brand-mint text-brand-greenDeep font-semibold text-xs" data-testid="patri-export-xlsx"><FileSpreadsheet className="w-3.5 h-3.5" /> Excel</button>
                  <button onClick={() => window.open(`${BACKEND_URL}/api/finances/patrimoine-export?format=pdf&owner=${encodeURIComponent(owner)}&properties=${encodeURIComponent(propsParam)}`, "_blank")} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep font-semibold text-xs" data-testid="patri-export-pdf"><Download className="w-3.5 h-3.5" /> PDF</button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-brand-softer text-left text-brand-green/60 text-xs">
                    <tr>
                      <th className="px-4 py-3 font-medium">Bien</th>
                      <th className="px-4 py-3 font-medium">Type</th>
                      <th className="px-4 py-3 font-medium">Localisation</th>
                      <th className="px-4 py-3 font-medium text-right">Surface</th>
                      <th className="px-4 py-3 font-medium text-center">DPE</th>
                      <th className="px-4 py-3 font-medium text-right">Prix d'achat</th>
                      <th className="px-4 py-3 font-medium text-right">Prix / m²</th>
                      <th className="px-4 py-3 font-medium text-right">Estimation</th>
                      <th className="px-4 py-3 font-medium text-right">Plus-value</th>
                      <th className="px-4 py-3 font-medium text-right">Impôt plus-value</th>
                      <th className="px-4 py-3 font-medium text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-soft">
                    {patrimoine.items.map((it) => (
                      <tr key={it.id} className="text-brand-green/80 hover:bg-brand-softer/40" data-testid={`patri-row-${it.id}`}>
                        <td className="px-4 py-3 font-medium text-brand-greenDeep">{it.name}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{it.type || "—"}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{[it.postal_code, it.city].filter(Boolean).join(" ") || <span className="text-brand-green/40">—</span>}{it.insee ? <span className="block text-[10px] text-brand-green/40">INSEE {it.insee}</span> : null}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">{it.surface ? `${fmt(it.surface)} m²` : "—"}</td>
                        <td className="px-4 py-3 text-center">{it.energy_class || "—"}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">{it.purchase_price ? `${fmt(it.purchase_price)} €` : "—"}</td>
                        {it.estimated_value ? (
                          <>
                            <td className="px-4 py-3 text-right whitespace-nowrap">
                              <CalcTip title="Prix au m² (marché)"
                                rows={[
                                  { label: "Prix médian au m²", value: `${fmt(it.price_per_m2)} €`, strong: true },
                                  { label: "Fourchette (min–max)", value: `${fmt(it.low_ppm)} – ${fmt(it.high_ppm)} €` },
                                  { label: "Ventes comparables", value: `${it.comps}` },
                                  { label: "Source", value: SRC_LABEL[it.source] || "DVF open-data" },
                                ]}
                                note="Prix médian au m² des ventes récentes proches (même commune, surface comparable), valeurs aberrantes exclues.">
                                <span>{fmt(it.price_per_m2)} €</span>
                              </CalcTip>
                            </td>
                            <td className="px-4 py-3 text-right whitespace-nowrap font-semibold text-brand-greenDeep">
                              <CalcTip title="Estimation du bien"
                                rows={[
                                  { label: "Prix au m²", value: `${fmt(it.price_per_m2)} €` },
                                  { label: "× Surface", value: `${fmt(it.surface)} m²` },
                                  { label: "Fourchette basse", value: `${fmt(it.low_value)} €` },
                                  { label: "Fourchette haute", value: `${fmt(it.high_value)} €` },
                                ]}
                                total={{ label: "Estimation", value: `${fmt(it.estimated_value)} €` }}
                                note="Estimation = prix médian au m² × surface du bien. La fourchette reflète la dispersion des ventes comparables.">
                                <span>{fmt(it.estimated_value)} €</span>
                              </CalcTip>
                              <span className="block text-[10px] text-brand-green/40 font-normal">{fmt(it.low_value)} – {fmt(it.high_value)} € · {it.comps} vente(s)</span>
                            </td>
                            <td className="px-4 py-3 text-right whitespace-nowrap font-semibold">
                              {it.plus_value != null ? (
                                <CalcTip title="Plus-value latente"
                                  rows={[
                                    { label: "Estimation", value: `${fmt(it.estimated_value)} €` },
                                    { label: "− Prix d'achat", value: `${fmt(it.purchase_price)} €` },
                                  ]}
                                  total={{ label: `Plus-value (${it.plus_value >= 0 ? "+" : ""}${it.plus_value_pct}%)`, value: `${it.plus_value >= 0 ? "+" : ""}${fmt(it.plus_value)} €` }}
                                  note="Plus-value latente indicative (avant impôt) = valeur estimée − prix d'achat. Le pourcentage est rapporté au prix d'achat.">
                                  <span className={it.plus_value >= 0 ? "text-brand-emerald" : "text-red-600"}>{it.plus_value >= 0 ? "+" : ""}{fmt(it.plus_value)} €<span className="block text-[10px] font-normal opacity-70">{it.plus_value >= 0 ? "+" : ""}{it.plus_value_pct}%</span></span>
                                </CalcTip>
                              ) : <span className="text-brand-green/40 text-xs">Prix d'achat ?</span>}
                            </td>
                          </>
                        ) : (
                          <td className="px-4 py-3 text-xs text-amber-700" colSpan={3} data-testid={`patri-msg-${it.id}`}>{it.message}</td>
                        )}
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {it.fiscal ? (
                            it.fiscal.exonere ? (
                              <CalcTip title="Plus-value exonérée"
                                rows={[
                                  { label: "Prix d'acq. majoré", value: `${fmt(it.fiscal.prix_acq_majore)} €` },
                                  { label: "Plus-value brute", value: `${fmt(it.fiscal.pv_brute)} €` },
                                  { label: "Motif d'exonération", value: it.fiscal.exoneration_motif || "—", strong: true },
                                ]}
                                total={{ label: "Plus-value nette", value: `${fmt(it.fiscal.net)} €` }}
                                note="Aucun impôt : la totalité de la plus-value brute vous revient (net = plus-value brute).">
                                <span className="inline-flex flex-col items-end">
                                  <span className="inline-flex px-2 py-0.5 rounded-full bg-brand-emerald/10 text-brand-emerald text-[11px] font-semibold">Exonéré</span>
                                  <span className="text-[10px] text-brand-green/40 font-normal mt-0.5">{it.fiscal.exoneration_motif}</span>
                                </span>
                              </CalcTip>
                            ) : it.fiscal.pv_brute > 0 ? (
                              <CalcTip title="Impôt sur la plus-value"
                                rows={[
                                  { label: "Prix d'achat", value: `${fmt(it.purchase_price)} €` },
                                  { label: `+ Frais acq. (${it.fiscal.frais_mode})`, value: `${fmt(it.fiscal.frais_acq)} €` },
                                  { label: `+ Travaux (${it.fiscal.travaux_mode})`, value: `${fmt(it.fiscal.travaux)} €` },
                                  { label: "= Prix d'acq. majoré", value: `${fmt(it.fiscal.prix_acq_majore)} €`, strong: true },
                                  { label: "Plus-value brute", value: `${fmt(it.fiscal.pv_brute)} €`, strong: true },
                                  { label: `Abattement IR (${it.fiscal.years} ans)`, value: `${Math.round((it.fiscal.abattement_ir_pct || 0) * 100) / 100}%` },
                                  { label: `Abattement PS (${it.fiscal.years} ans)`, value: `${Math.round((it.fiscal.abattement_ps_pct || 0) * 100) / 100}%` },
                                  { label: "Base IR → IR 19%", value: `${fmt(it.fiscal.impot_ir)} €` },
                                  { label: "Base PS → PS 17,2%", value: `${fmt(it.fiscal.impot_ps)} €` },
                                  ...(it.fiscal.surtaxe ? [{ label: "Surtaxe (> 50 000 €)", value: `${fmt(it.fiscal.surtaxe)} €` }] : []),
                                  { label: "= Impôt total", value: `${fmt(it.fiscal.total_impot)} €`, strong: true },
                                ]}
                                total={{ label: "Net (PV brute − impôt)", value: `${fmt(it.fiscal.net)} €` }}
                                note={`${it.fiscal.projected ? "Simulation" : "Calcul"} au ${it.fiscal.computed_on ? new Date(it.fiscal.computed_on).toLocaleDateString("fr-FR") : ""} · Règles service-public F10864. « net » = plus-value qui vous reste après impôt.`}>
                                <span className="font-semibold text-brand-greenDeep">
                                  {fmt(it.fiscal.total_impot)} €
                                  <span className="block text-[10px] text-brand-green/40 font-normal">net {fmt(it.fiscal.net)} €</span>
                                </span>
                              </CalcTip>
                            ) : <span className="text-brand-green/40 text-xs">—</span>
                          ) : <span className="text-brand-green/40 text-xs">Date d'achat ?</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button onClick={() => setEditProp(it)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-soft hover:bg-brand-mint text-brand-greenDeep font-semibold text-xs" data-testid={`patri-edit-${it.id}`}>Compléter</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="font-semibold text-brand-greenDeep bg-brand-lime/20 border-t-2 border-brand-lime" data-testid="patri-total-row">
                      <td className="px-4 py-3" colSpan={3}>Totaux ({patrimoine.items.length} bien{patrimoine.items.length > 1 ? "s" : ""})</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap" data-testid="patri-total-surface">{fmt(patrimoine.items.reduce((s, i) => s + (i.surface || 0), 0))} m²</td>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3 text-right whitespace-nowrap" data-testid="patri-total-purchase">{fmt(patrimoine.total_purchase)} €</td>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3 text-right whitespace-nowrap" data-testid="patri-total-value">{fmt(patrimoine.total_estimated)} €</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap"><span className={(patrimoine.total_plus_value || 0) >= 0 ? "text-brand-emerald" : "text-red-600"} data-testid="patri-total-pv">{(patrimoine.total_plus_value || 0) >= 0 ? "+" : ""}{fmt(patrimoine.total_plus_value)} €</span></td>
                      <td className="px-4 py-3 text-right whitespace-nowrap" data-testid="patri-total-tax">{fmt(patrimoine.items.reduce((s, i) => s + ((i.fiscal && i.fiscal.total_impot) || 0), 0))} €</td>
                      <td className="px-4 py-3"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <p className="px-5 py-3 text-[11px] text-brand-green/45 border-t border-brand-soft">Estimation indicative calculée à partir de la médiane des prix au m² des ventes réelles (DVF+) de la commune sur les 4 dernières années, pour un type de bien et une surface comparables. Ne constitue pas une évaluation professionnelle.</p>
            </div>
          )}
        </div>
        </TooltipProvider>
      )}

      {editProp && <PatrimoineEditModal item={editProp} onClose={() => setEditProp(null)} onSaved={() => { setEditProp(null); loadPatrimoine(); }} />}

      {/* BILAN */}
      {!loading && tab === "bilan" && bilan && (
        <div className="space-y-5" data-testid="bilan-content">
          <div className="flex justify-end gap-2">
            <button onClick={() => exportBilan("pdf")} disabled={exporting} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep font-semibold text-sm disabled:opacity-60" data-testid="export-pdf-btn">
              {exporting === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Export PDF
            </button>
            <button onClick={() => exportBilan("xlsx")} disabled={exporting} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep font-semibold text-sm disabled:opacity-60" data-testid="export-xlsx-btn">
              {exporting === "xlsx" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Export Excel
            </button>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <TotalCard icon={TrendingUp} label="Revenu brut total" value={bilan.revenue_total} tone="green" prev={bilan.prev.revenue_total} prevLabel={bilan.prev_year} delta={bilan.delta.revenue} />
            <TotalCard icon={TrendingDown} label="Dépenses totales" value={bilan.expense_total} tone="orange" prev={bilan.prev.expense_total} prevLabel={bilan.prev_year} delta={bilan.delta.expense} />
            <TotalCard icon={PiggyBank} label={`Résultat net ${bilan.year}`} value={bilan.net} tone="gold" prev={bilan.prev.net} prevLabel={bilan.prev_year} delta={bilan.delta.net} />
          </div>
          <div className="grid lg:grid-cols-2 gap-4">
            <CategoryList title="Revenus par catégorie" rows={bilan.revenue_by_category} tone="green" prevYear={bilan.prev_year} />
            <CategoryList title="Dépenses par catégorie" rows={bilan.expense_by_category} tone="orange" prevYear={bilan.prev_year} />
          </div>
          <div className="bg-white rounded-2xl border border-brand-soft p-5" data-testid="bilan-chart">
            <h4 className="font-semibold text-brand-greenDeep mb-4">Évolution mensuelle {bilan.year}</h4>
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={bilan.monthly} margin={{ left: 10, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#efe7d6" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#5a5a52" }} tickFormatter={(m) => m.slice(0, 3)} />
                <YAxis tick={{ fontSize: 11, fill: "#5a5a52" }} width={72} tickFormatter={fmt} />
                <Tooltip formatter={(v) => `${fmt2(v)} €`} />
                <Legend />
                <Bar dataKey="revenue" name="Revenus" fill="#2f8f6b" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expense" name="Dépenses" fill="#e08a4a" radius={[4, 4, 0, 0]} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Rapport financier IA */}
          <div className="bg-gradient-to-br from-brand-greenDeep to-brand-green rounded-2xl p-6 text-white" data-testid="ai-report-card">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-start gap-3">
                <span className="w-11 h-11 rounded-xl bg-brand-lime/20 flex items-center justify-center shrink-0"><Sparkles className="w-5 h-5 text-brand-lime" /></span>
                <div>
                  <h4 className="font-semibold text-lg">Rapport financier IA</h4>
                  <p className="text-sm text-white/70 mt-0.5 max-w-lg">Générez un compte-rendu de gestion rédigé par l'IA à partir du bilan affiché ci-dessus ({buildAiScope()}).</p>
                </div>
              </div>
              <button onClick={generateAiReport} disabled={aiBusy} className="shrink-0 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm disabled:opacity-60" data-testid="ai-report-generate-btn">
                {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {aiReport ? "Régénérer" : "Générer le rapport IA"}
              </button>
            </div>

            {aiReport && (
              <div className="mt-5 bg-white rounded-xl p-5 text-brand-green space-y-4" data-testid="ai-report-result">
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-emerald-50 rounded-xl px-2 py-3"><p className="text-xs text-brand-green/45">Revenus</p><p className="font-semibold text-emerald-700">{(aiReport.kpis?.revenus || 0).toLocaleString("fr-FR")} €</p></div>
                  <div className="bg-red-50 rounded-xl px-2 py-3"><p className="text-xs text-brand-green/45">Charges</p><p className="font-semibold text-red-600">{(aiReport.kpis?.charges || 0).toLocaleString("fr-FR")} €</p></div>
                  <div className="bg-brand-lime/20 rounded-xl px-2 py-3"><p className="text-xs text-brand-green/45">Résultat net</p><p className="font-semibold text-brand-greenDeep">{(aiReport.kpis?.net || 0).toLocaleString("fr-FR")} €</p></div>
                </div>
                <p className="text-sm text-brand-green/80 leading-relaxed" data-testid="ai-report-summary">{aiReport.summary}</p>
                {aiReport.highlights?.length > 0 && (
                  <div><p className="text-sm font-semibold text-brand-greenDeep mb-1">Points clés</p><ul className="list-disc pl-5 text-sm text-brand-green/75 space-y-1">{aiReport.highlights.map((h, i) => <li key={i}>{h}</li>)}</ul></div>
                )}
                {aiReport.market_comparison && <p className="text-sm text-brand-green/60 italic bg-brand-softer rounded-xl px-3 py-2">📊 {aiReport.market_comparison}</p>}
                {aiReport.recommendations?.length > 0 && (
                  <div><p className="text-sm font-semibold text-brand-greenDeep mb-1">Recommandations</p><ul className="list-disc pl-5 text-sm text-brand-green/75 space-y-1">{aiReport.recommendations.map((r, i) => <li key={i}>{r}</li>)}</ul></div>
                )}
                <button onClick={exportAiReportPdf} disabled={aiBusy} className="w-full py-2.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="ai-report-pdf-btn">
                  <Download className="w-4 h-4" /> Exporter le rapport en PDF
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* PREVISIONNEL */}
      {!loading && tab === "previsionnel" && prev && (
        <div className="space-y-5" data-testid="previsionnel-content">
          <div className="flex justify-end gap-2">
            <button onClick={() => exportPrevisionnel("pdf")} disabled={exporting} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep font-semibold text-sm disabled:opacity-60" data-testid="pv-export-pdf-btn">
              {exporting === "pv-pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Export PDF
            </button>
            <button onClick={() => exportPrevisionnel("xlsx")} disabled={exporting} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep font-semibold text-sm disabled:opacity-60" data-testid="pv-export-xlsx-btn">
              {exporting === "pv-xlsx" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Export Excel
            </button>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <TotalCard icon={TrendingUp} label="Revenus prévus" value={prev.revenue_total} tone="green" />
            <TotalCard icon={TrendingDown} label="Dépenses prévues" value={prev.expense_total} tone="orange" />
            <TotalCard icon={PiggyBank} label="Résultat net prévisionnel" value={prev.net} tone="gold" />
          </div>
          <p className="text-xs text-brand-green/50 -mt-2">Projection sur {prev.months} mois · loyers des baux actifs, charges récurrentes et échéances de prêts.</p>

          {/* Scénarios prévisionnels */}
          <div className="bg-white rounded-2xl border border-brand-soft p-5" data-testid="scenario-card">
            <h4 className="font-semibold text-brand-greenDeep mb-1 flex items-center gap-2"><Sparkles className="w-4 h-4 text-brand-limeDark" /> Scénarios prévisionnels</h4>
            <p className="text-xs text-brand-green/50 mb-4">Simulez l'impact d'un nouveau loyer et/ou d'un nouveau prêt sur votre projection.</p>
            <div className="grid md:grid-cols-2 gap-5">
              <div className="rounded-xl border border-brand-soft p-4">
                <p className="text-sm font-semibold text-brand-greenDeep mb-3 flex items-center gap-1.5"><TrendingUp className="w-4 h-4 text-brand-emerald" /> Nouveau loyer</p>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block"><span className="block text-[11px] text-brand-green/70 mb-1">Loyer mensuel (€)</span><input type="number" min="0" value={scForm.rent_amount} onChange={(e) => scSet("rent_amount", e.target.value)} className="inp" data-testid="sc-rent-amount" /></label>
                  <label className="block"><span className="block text-[11px] text-brand-green/70 mb-1">À partir du</span><input type="date" value={scForm.rent_start} onChange={(e) => scSet("rent_start", e.target.value)} className="inp" data-testid="sc-rent-start" /></label>
                </div>
              </div>
              <div className="rounded-xl border border-brand-soft p-4">
                <p className="text-sm font-semibold text-brand-greenDeep mb-3 flex items-center gap-1.5"><TrendingDown className="w-4 h-4 text-orange-500" /> Nouveau prêt</p>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block"><span className="block text-[11px] text-brand-green/70 mb-1">Montant (€)</span><input type="number" min="0" value={scForm.loan_amount} onChange={(e) => scSet("loan_amount", e.target.value)} className="inp" data-testid="sc-loan-amount" /></label>
                  <label className="block"><span className="block text-[11px] text-brand-green/70 mb-1">Durée (mois)</span><input type="number" min="0" value={scForm.loan_months} onChange={(e) => scSet("loan_months", e.target.value)} className="inp" data-testid="sc-loan-months" /></label>
                  <label className="block"><span className="block text-[11px] text-brand-green/70 mb-1">Taux (%)</span><input type="number" step="0.01" min="0" value={scForm.loan_rate} onChange={(e) => scSet("loan_rate", e.target.value)} className="inp" data-testid="sc-loan-rate" /></label>
                  <label className="block"><span className="block text-[11px] text-brand-green/70 mb-1">Assurance (%)</span><input type="number" step="0.01" min="0" value={scForm.loan_ins} onChange={(e) => scSet("loan_ins", e.target.value)} className="inp" data-testid="sc-loan-ins" /></label>
                  <label className="block col-span-2"><span className="block text-[11px] text-brand-green/70 mb-1">Début du prêt</span><input type="date" value={scForm.loan_start} onChange={(e) => scSet("loan_start", e.target.value)} className="inp" data-testid="sc-loan-start" /></label>
                </div>
              </div>
            </div>
            <button onClick={runScenario} disabled={scBusy} className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm disabled:opacity-60" data-testid="sc-run-btn">
              {scBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Simuler l'impact
            </button>

            {scenario && (
              <div className="mt-5 space-y-4" data-testid="scenario-result">
                <div className="flex justify-end">
                  <button onClick={() => { setScenarioName(""); setNameModal(true); }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-limeDark bg-brand-lime/20 hover:bg-brand-lime/40 text-brand-greenDeep font-semibold text-sm" data-testid="sc-save-btn">
                    <Save className="w-4 h-4" /> Enregistrer ce scénario
                  </button>
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div className="rounded-xl bg-brand-softer/60 p-4"><p className="text-[11px] text-brand-green/50">Revenus prévus</p><p className="font-semibold text-brand-greenDeep">{fmt2(scenario.scenario.revenue_total)} €</p><p className={`text-xs font-semibold ${scenario.delta.revenue >= 0 ? "text-brand-emerald" : "text-red-600"}`}>{scenario.delta.revenue >= 0 ? "+" : ""}{fmt2(scenario.delta.revenue)} €</p></div>
                  <div className="rounded-xl bg-brand-softer/60 p-4"><p className="text-[11px] text-brand-green/50">Dépenses prévues</p><p className="font-semibold text-brand-greenDeep">{fmt2(scenario.scenario.expense_total)} €</p><p className={`text-xs font-semibold ${scenario.delta.expense <= 0 ? "text-brand-emerald" : "text-orange-600"}`}>{scenario.delta.expense >= 0 ? "+" : ""}{fmt2(scenario.delta.expense)} €</p></div>
                  <div className="rounded-xl bg-brand-lime/20 p-4"><p className="text-[11px] text-brand-green/50">Résultat net prévisionnel</p><p className="font-semibold text-brand-greenDeep">{fmt2(scenario.scenario.net)} €</p><p className={`text-xs font-semibold ${scenario.delta.net >= 0 ? "text-brand-emerald" : "text-red-600"}`} data-testid="sc-delta-net">{scenario.delta.net >= 0 ? "+" : ""}{fmt2(scenario.delta.net)} € vs actuel</p></div>
                </div>
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={scenario.scenario.monthly.map((m, i) => ({ label: m.label, base: scenario.base.monthly[i]?.cumulative_net || 0, scenario: m.cumulative_net }))} margin={{ left: 10, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#efe7d6" />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#5a5a52" }} tickFormatter={(l) => l.slice(0, 3)} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 11, fill: "#5a5a52" }} width={72} tickFormatter={fmt} />
                    <Tooltip formatter={(v) => `${fmt2(v)} €`} />
                    <Legend />
                    <Line type="monotone" dataKey="base" name="Net cumulé actuel" stroke="#9aa39a" strokeWidth={2} strokeDasharray="5 4" dot={false} />
                    <Line type="monotone" dataKey="scenario" name="Net cumulé scénario" stroke="#c9a24b" strokeWidth={2.5} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Rapport prévisionnel IA */}
          <div className="bg-gradient-to-br from-brand-greenDeep to-brand-green rounded-2xl p-6 text-white" data-testid="pv-ai-report-card">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-start gap-3">
                <span className="w-11 h-11 rounded-xl bg-brand-lime/20 flex items-center justify-center shrink-0"><Sparkles className="w-5 h-5 text-brand-lime" /></span>
                <div>
                  <h4 className="font-semibold text-lg">Rapport prévisionnel IA</h4>
                  <p className="text-sm text-white/70 mt-0.5 max-w-lg">{scenario ? "Analyse IA de l'impact du scénario simulé et de son cashflow projeté." : "Analyse IA de votre projection et de son cashflow. Simulez un scénario ci-dessus pour l'inclure."}</p>
                </div>
              </div>
              <button onClick={generatePvReport} disabled={pvBusy} className="shrink-0 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm disabled:opacity-60" data-testid="pv-ai-generate-btn">
                {pvBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {pvReport ? "Régénérer" : "Générer le rapport IA"}
              </button>
            </div>

            {pvReport && (
              <div className="mt-5 bg-white rounded-xl p-5 text-brand-green space-y-4" data-testid="pv-ai-result">
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-bold px-3 py-1 rounded-full ${pvReport.verdict === "Favorable" ? "bg-emerald-100 text-emerald-700" : pvReport.verdict === "Défavorable" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`} data-testid="pv-ai-verdict">{pvReport.verdict}</span>
                  <span className="text-sm text-brand-green/55">{pvReport.scope}</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                  <div className="bg-brand-lime/20 rounded-xl px-2 py-3"><p className="text-[11px] text-brand-green/45">Net cumulé final</p><p className="font-semibold text-brand-greenDeep">{(pvReport.cashflow?.final_net || 0).toLocaleString("fr-FR")} €</p></div>
                  <div className="bg-brand-softer rounded-xl px-2 py-3"><p className="text-[11px] text-brand-green/45">Point bas</p><p className="font-semibold text-brand-greenDeep">{(pvReport.cashflow?.min_val || 0).toLocaleString("fr-FR")} €</p><p className="text-[10px] text-brand-green/45">{pvReport.cashflow?.min_label || "—"}</p></div>
                  <div className="bg-brand-softer rounded-xl px-2 py-3"><p className="text-[11px] text-brand-green/45">Trésorerie positive</p><p className="font-semibold text-brand-greenDeep">{pvReport.cashflow?.first_positive_label || "Jamais"}</p></div>
                  <div className="bg-brand-softer rounded-xl px-2 py-3"><p className="text-[11px] text-brand-green/45">Mois négatifs</p><p className="font-semibold text-brand-greenDeep">{pvReport.cashflow?.negative_months ?? 0}</p></div>
                </div>
                <p className="text-sm text-brand-green/80 leading-relaxed" data-testid="pv-ai-summary">{pvReport.summary}</p>
                {pvReport.cashflow_analysis && (
                  <div className="bg-brand-softer rounded-xl px-3 py-3"><p className="text-sm font-semibold text-brand-greenDeep mb-1">💸 Analyse du cashflow projeté</p><p className="text-sm text-brand-green/75 leading-relaxed">{pvReport.cashflow_analysis}</p></div>
                )}
                {pvReport.highlights?.length > 0 && (
                  <div><p className="text-sm font-semibold text-brand-greenDeep mb-1">Points clés</p><ul className="list-disc pl-5 text-sm text-brand-green/75 space-y-1">{pvReport.highlights.map((h, i) => <li key={i}>{h}</li>)}</ul></div>
                )}
                {pvReport.recommendations?.length > 0 && (
                  <div><p className="text-sm font-semibold text-brand-greenDeep mb-1">Recommandations</p><ul className="list-disc pl-5 text-sm text-brand-green/75 space-y-1">{pvReport.recommendations.map((r, i) => <li key={i}>{r}</li>)}</ul></div>
                )}
              </div>
            )}
          </div>

          {/* Scénarios enregistrés & comparaison */}
          {saved.length > 0 && (
            <div className="bg-white rounded-2xl border border-brand-soft p-5" data-testid="saved-scenarios-card">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <h4 className="font-semibold text-brand-greenDeep flex items-center gap-2"><GitCompare className="w-4 h-4 text-brand-limeDark" /> Scénarios enregistrés</h4>
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-1.5 text-xs text-brand-green/70 cursor-pointer" data-testid="include-base-toggle">
                    <input type="checkbox" checked={includeBase} onChange={(e) => setIncludeBase(e.target.checked)} className="w-4 h-4 accent-brand-limeDark" />
                    Inclure la projection de base
                  </label>
                  <button onClick={runCompare} disabled={compareBusy || (selSaved.length === 0 && !includeBase)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-greenDeep hover:bg-brand-emerald text-white font-semibold text-sm disabled:opacity-50" data-testid="compare-btn">
                    {compareBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitCompare className="w-4 h-4" />} Comparer ({selSaved.length}{includeBase ? "+base" : ""})
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                {saved.map((s) => {
                  const on = selSaved.includes(s.id);
                  const bits = [];
                  if (s.extra_rent) bits.push(`+ loyer ${fmt2(s.extra_rent.amount)} €`);
                  if (s.extra_loan) bits.push(`+ prêt ${fmt2(s.extra_loan.amount)} €`);
                  return (
                    <div key={s.id} className={`flex items-center gap-3 rounded-xl border p-3 ${on ? "border-brand-limeDark bg-brand-lime/10" : "border-brand-soft"}`} data-testid={`saved-row-${s.id}`}>
                      <input type="checkbox" checked={on} onChange={() => toggleSaved(s.id)} className="w-4 h-4 accent-brand-limeDark cursor-pointer" data-testid={`saved-select-${s.id}`} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-brand-greenDeep truncate">{s.name}</p>
                        <p className="text-[11px] text-brand-green/50 truncate">{PERIODS.find((p) => p.code === s.period)?.label || s.period} · {bits.join(" · ") || "—"}</p>
                      </div>
                      <button onClick={() => deleteSaved(s.id)} className="p-1.5 rounded-full hover:bg-red-50 text-brand-green/40 hover:text-red-600" data-testid={`saved-del-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  );
                })}
              </div>
              {compareData && compareData.length > 0 && (
                <div className="mt-5 space-y-4" data-testid="compare-result">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-brand-softer text-left text-brand-green/60">
                        <tr>
                          <th className="px-4 py-2.5 font-medium">Scénario</th>
                          <th className="px-4 py-2.5 font-medium text-right">Revenus</th>
                          <th className="px-4 py-2.5 font-medium text-right">Dépenses</th>
                          <th className="px-4 py-2.5 font-medium text-right">Net</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-brand-soft">
                        {compareData.map((s, i) => (
                          <tr key={s.id} className="text-brand-green/80">
                            <td className="px-4 py-2.5 font-medium text-brand-greenDeep"><span className="inline-block w-2.5 h-2.5 rounded-full mr-2" style={{ background: s.is_base ? "#9aa39a" : COMPARE_COLORS[i % COMPARE_COLORS.length] }} />{s.name}</td>
                            <td className="px-4 py-2.5 text-right text-brand-emerald">{fmt2(s.revenue_total)} €</td>
                            <td className="px-4 py-2.5 text-right text-orange-600">{fmt2(s.expense_total)} €</td>
                            <td className={`px-4 py-2.5 text-right font-semibold ${s.net >= 0 ? "text-brand-greenDeep" : "text-red-600"}`}>{fmt2(s.net)} €</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={compareChart} margin={{ left: 10, right: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#efe7d6" />
                      <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#5a5a52" }} tickFormatter={(l) => l.slice(0, 3)} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 11, fill: "#5a5a52" }} width={72} tickFormatter={fmt} />
                      <Tooltip formatter={(v) => `${fmt2(v)} €`} />
                      <Legend />
                      {compareData.map((s, i) => (
                        <Line key={s.id} type="monotone" dataKey={s.id} name={s.name} stroke={s.is_base ? "#9aa39a" : COMPARE_COLORS[i % COMPARE_COLORS.length]} strokeWidth={s.is_base ? 2 : 2.5} strokeDasharray={s.is_base ? "5 4" : undefined} dot={false} connectNulls />
                      ))}
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}

          <div className="bg-white rounded-2xl border border-brand-soft p-5" data-testid="previsionnel-chart">
            <h4 className="font-semibold text-brand-greenDeep mb-4 flex items-center gap-2"><LineIcon className="w-4 h-4" /> Projection des flux</h4>
            <ResponsiveContainer width="100%" height={340}>
              <ComposedChart data={prev.monthly} margin={{ left: 10, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#efe7d6" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#5a5a52" }} tickFormatter={(l) => l.slice(0, 3)} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11, fill: "#5a5a52" }} width={72} tickFormatter={fmt} />
                <Tooltip formatter={(v) => `${fmt2(v)} €`} />
                <Legend />
                <Bar dataKey="revenue" name="Revenus" fill="#2f8f6b" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expense" name="Dépenses" fill="#e08a4a" radius={[4, 4, 0, 0]} />
                <Line type="monotone" dataKey="cumulative_net" name="Net cumulé" stroke="#c9a24b" strokeWidth={2.5} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white rounded-2xl border border-brand-soft overflow-hidden" data-testid="previsionnel-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-brand-softer text-left text-brand-green/60">
                  <tr>
                    <th className="px-5 py-3 font-medium">Mois</th>
                    <th className="px-5 py-3 font-medium text-right">Revenus</th>
                    <th className="px-5 py-3 font-medium text-right">Dépenses</th>
                    <th className="px-5 py-3 font-medium text-right">Net</th>
                    <th className="px-5 py-3 font-medium text-right">Net cumulé</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-soft">
                  {prev.monthly.map((m) => (
                    <tr key={m.label} className="text-brand-green/80">
                      <td className="px-5 py-2.5 font-medium text-brand-greenDeep">{m.label}</td>
                      <td className="px-5 py-2.5 text-right text-brand-emerald">{fmt2(m.revenue)} €</td>
                      <td className="px-5 py-2.5 text-right text-orange-600">{fmt2(m.expense)} €</td>
                      <td className={`px-5 py-2.5 text-right font-semibold ${m.net >= 0 ? "text-brand-greenDeep" : "text-red-600"}`}>{fmt2(m.net)} €</td>
                      <td className={`px-5 py-2.5 text-right ${m.cumulative_net >= 0 ? "text-brand-greenDeep" : "text-red-600"}`}>{fmt2(m.cumulative_net)} €</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <style>{`.inp{width:100%;padding:0.55rem 0.85rem;border-radius:0.8rem;border:1px solid #efe7d6;outline:none;background:#fff;font-size:0.85rem}.inp:focus{border-color:#c9a24b;box-shadow:0 0 0 3px rgba(201,162,75,.2)}`}</style>

      {nameModal && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={() => setNameModal(false)}>
          <div className="bg-white rounded-3xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="name-scenario-modal">
            <div className="flex items-center justify-between p-6 pb-4 border-b border-brand-soft">
              <h3 className="font-display text-xl font-semibold text-brand-greenDeep flex items-center gap-2"><Save className="w-5 h-5 text-brand-limeDark" /> Enregistrer le scénario</h3>
              <button onClick={() => setNameModal(false)} className="text-brand-green/40 hover:text-brand-greenDeep"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <label className="block">
                <span className="block text-xs font-medium text-brand-green/70 mb-1.5">Nom du scénario</span>
                <input autoFocus value={scenarioName} onChange={(e) => setScenarioName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveScenario(); }} placeholder="Ex. Achat studio + prêt 15 ans" className="inp" data-testid="name-scenario-input" />
              </label>
              <div className="flex justify-end gap-2">
                <button onClick={() => setNameModal(false)} className="px-5 py-2.5 rounded-full border border-brand-soft text-brand-green/70 font-semibold text-sm hover:bg-brand-softer">Annuler</button>
                <button onClick={saveScenario} disabled={savingName} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm disabled:opacity-60" data-testid="name-scenario-save-btn">
                  {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PatrimoineEditModal({ item, onClose, onSaved }) {
  const [f, setF] = useState({
    address: item.street_address ?? item.address ?? "",
    postal_code: item.postal_code || "",
    city: item.city || "",
    surface: item.surface || "",
    rooms: item.rooms || "",
    purchase_price: item.purchase_price || "",
    purchase_date: item.purchase_date || "",
    sale_price: item.sale_price || "",
    acquisition_fees: item.acquisition_fees || "",
    works_amount: item.works_amount || "",
    is_primary_residence: item.is_primary_residence || false,
    sale_date: item.sale_date || "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    setLoading(true);
    try {
      await api.patch(`/properties/${item.id}/patrimoine`, {
        address: f.address, postal_code: f.postal_code, city: f.city,
        surface: Number(f.surface) || 0, rooms: Number(f.rooms) || 0,
        purchase_price: Number(f.purchase_price) || 0,
        purchase_date: f.purchase_date || null, sale_price: Number(f.sale_price) || 0,
        acquisition_fees: Number(f.acquisition_fees) || 0, works_amount: Number(f.works_amount) || 0,
        is_primary_residence: !!f.is_primary_residence, sale_date: f.sale_date || null,
      });
      toast.success("Bien mis à jour — estimation recalculée");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="patri-edit-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-semibold text-brand-greenDeep flex items-center gap-2"><Home className="w-5 h-5" /> Compléter le bien</h3>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-brand-soft"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-brand-green/55 mb-4">{item.name} — renseignez l'adresse, la surface et le prix d'achat pour activer l'estimation DVF et la plus-value.</p>
        <div className="space-y-3">
          <label className="block"><span className="block text-xs font-medium text-brand-green/70 mb-1">Adresse</span>
            <input value={f.address} onChange={(e) => set("address", e.target.value)} className="inp" placeholder="10 Rue de Rivoli" data-testid="patri-f-address" /></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="block text-xs font-medium text-brand-green/70 mb-1">Code postal</span>
              <input value={f.postal_code} onChange={(e) => set("postal_code", e.target.value)} className="inp" placeholder="75004" data-testid="patri-f-postal" /></label>
            <label className="block"><span className="block text-xs font-medium text-brand-green/70 mb-1">Ville</span>
              <input value={f.city} onChange={(e) => set("city", e.target.value)} className="inp" placeholder="Paris" data-testid="patri-f-city" /></label>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <label className="block"><span className="block text-xs font-medium text-brand-green/70 mb-1">Surface (m²)</span>
              <input type="number" value={f.surface} onChange={(e) => set("surface", e.target.value)} className="inp" data-testid="patri-f-surface" /></label>
            <label className="block"><span className="block text-xs font-medium text-brand-green/70 mb-1">Pièces</span>
              <input type="number" value={f.rooms} onChange={(e) => set("rooms", e.target.value)} className="inp" data-testid="patri-f-rooms" /></label>
            <label className="block"><span className="block text-xs font-medium text-brand-green/70 mb-1">Prix d'achat (€)</span>
              <input type="number" value={f.purchase_price} onChange={(e) => set("purchase_price", e.target.value)} className="inp" data-testid="patri-f-price" /></label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="block text-xs font-medium text-brand-green/70 mb-1">Date d'achat</span>
              <input type="date" value={f.purchase_date} onChange={(e) => set("purchase_date", e.target.value)} className="inp" data-testid="patri-f-pdate" /></label>
            <label className="block"><span className="block text-xs font-medium text-brand-green/70 mb-1">Prix de vente estimé (€)</span>
              <input type="number" value={f.sale_price} onChange={(e) => set("sale_price", e.target.value)} className="inp" placeholder="défaut : estimation DVF" data-testid="patri-f-sale" /></label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="block text-xs font-medium text-brand-green/70 mb-1">Frais d'acquisition réels (€)</span>
              <input type="number" value={f.acquisition_fees} onChange={(e) => set("acquisition_fees", e.target.value)} className="inp" placeholder="défaut : forfait 7,5 %" data-testid="patri-f-fees" /></label>
            <label className="block"><span className="block text-xs font-medium text-brand-green/70 mb-1">Travaux réels (€)</span>
              <input type="number" value={f.works_amount} onChange={(e) => set("works_amount", e.target.value)} className="inp" placeholder="défaut : forfait 15 % si >5 ans" data-testid="patri-f-works" /></label>
          </div>
          <label className="flex items-center gap-2 text-sm text-brand-green/80 mt-1">
            <input type="checkbox" checked={f.is_primary_residence} onChange={(e) => set("is_primary_residence", e.target.checked)} data-testid="patri-f-primary" /> Résidence principale (plus-value exonérée d'impôt)
          </label>
          <label className="block"><span className="block text-xs font-medium text-brand-green/70 mb-1">Date de cession projetée (simulation)</span>
            <input type="date" value={f.sale_date} onChange={(e) => set("sale_date", e.target.value)} className="inp" placeholder="défaut : aujourd'hui" data-testid="patri-f-saledate" /></label>
        </div>
        <button onClick={submit} disabled={loading} className="mt-5 w-full py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm disabled:opacity-60 flex items-center justify-center gap-2" data-testid="patri-f-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Enregistrer
        </button>
      </div>
    </div>
  );
}
